/**
 * Admin · Section Enabled-Toggle
 * ------------------------------
 * Reusable Switch für „Sektion sichtbar" oben in jedem Section-Panel.
 * Gold-Track wenn on, Ink-Grau wenn off. Ausgeschaltet → Formularfelder
 * darunter `opacity: 0.5 · pointer-events: none · aria-disabled=true`
 * (wird vom Consumer via Prop `disabledStyleTarget` gerendert).
 *
 * Semantik: `enabled` ist `boolean | undefined`. `undefined` wird als
 * `true` behandelt (Bestandsdaten ohne Feld bleiben sichtbar). Erst ein
 * explizites `false` blendet aus (`!== false`).
 */
import type { ReactNode } from "react";
import { Eye, EyeOff } from "lucide-react";

type Props = {
    enabled: boolean | undefined;
    onChange: (next: boolean) => void;
    label?: string;
    testId?: string;
    hint?: ReactNode;
};

export default function SectionEnabledToggle({
    enabled,
    onChange,
    label = "Sektion sichtbar",
    testId = "section-enabled-toggle",
    hint,
}: Props) {
    // undefined == on (Bestandsdaten-Kompat) — Anzeige folgt der `!== false`
    // Semantik der Public-Seite.
    const isOn = enabled !== false;

    return (
        <div
            className="flex items-center gap-3 p-3 rounded-sm mb-4"
            style={{
                background: isOn
                    ? "hsl(var(--hf-gold-soft) / 0.35)"
                    : "hsl(var(--hf-line) / 0.4)",
                border: `1px solid ${
                    isOn
                        ? "hsl(var(--hf-gold-dark) / 0.35)"
                        : "hsl(var(--hf-line))"
                }`,
            }}
            data-testid={testId}
        >
            <button
                type="button"
                role="switch"
                aria-checked={isOn}
                onClick={() => onChange(!isOn)}
                className="relative inline-flex items-center h-6 w-11 rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                style={{
                    background: isOn ? "#C6A962" : "hsl(var(--hf-muted) / 0.6)",
                }}
                data-testid={`${testId}-switch`}
                data-state={isOn ? "on" : "off"}
            >
                <span
                    aria-hidden
                    className="inline-block h-5 w-5 rounded-full bg-white shadow transition-transform"
                    style={{
                        transform: isOn ? "translateX(22px)" : "translateX(2px)",
                    }}
                />
            </button>
            <div className="flex-1 min-w-0">
                <div
                    className="text-[13px] font-medium inline-flex items-center gap-1.5"
                    style={{ color: isOn ? "hsl(var(--hf-ink))" : "hsl(var(--hf-muted))" }}
                >
                    {isOn ? <Eye size={14} /> : <EyeOff size={14} />}
                    {label}
                    <span
                        className="ml-2 text-[11px] uppercase tracking-[0.12em]"
                        style={{ color: isOn ? "#8f7738" : "hsl(var(--hf-muted))" }}
                    >
                        {isOn ? "sichtbar" : "ausgeblendet"}
                    </span>
                </div>
                {hint && (
                    <div className="text-[11px] mt-0.5" style={{ color: "hsl(var(--hf-muted))" }}>
                        {hint}
                    </div>
                )}
            </div>
        </div>
    );
}
